import { useMemo } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { CategoryEntity } from '@actual-app/core/types/models';
import { useQuery } from '@tanstack/react-query';

import { leerHistoria } from '#components/mobile/historia/historia';
import { useCategories } from '#hooks/useCategories';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { aqlQuery } from '#queries/aqlQuery';

import type { CategoriaInfo, Categorias, Movimiento } from './calculos';
import { condicionNoIngreso, descontarNoIngresos } from './noIngreso';

type FilaConsulta = {
  date: string; // 'yyyy-MM'
  category: string | null;
  payee: string | null;
  amount: IntegerAmount;
};

/** Caducidad de la caché de movimientos (reutilizada entre páginas). */
const CADUCIDAD_MS = 30_000;

export function consultaMovimientos(
  desde: string,
  hasta: string,
  soloNoIngreso = false,
) {
  let consulta = q('transactions').filter({
    $and: [
      { date: { $transform: '$month', $gte: desde } },
      { date: { $transform: '$month', $lte: hasta } },
    ],
    // Solo cuentas del presupuesto…
    'account.offbudget': false,
    // …y sin traspasos entre cuentas del presupuesto (dinero que no sale).
    $or: [
      { 'payee.transfer_acct': null },
      { 'payee.transfer_acct.offbudget': true },
    ],
  });
  if (soloNoIngreso) {
    // Lo que no cuenta como ingreso (traspasos y `#noingreso`).
    consulta = consulta.filter(condicionNoIngreso);
  }
  return consulta
    .groupBy([{ $month: '$date' }, { $id: '$category' }, { $id: '$payee' }])
    .select([
      { date: { $month: '$date' } },
      { category: { $id: '$category.id' } },
      { payee: { $id: '$payee.id' } },
      { amount: { $sum: '$amount' } },
    ]);
}

/**
 * Salidas sueltas de un mes (sin agrupar), con los mismos filtros que
 * `consultaMovimientos`: solo cuentas del presupuesto y sin traspasos.
 */
export function consultaSalidasDelMes(mes: string) {
  return q('transactions')
    .filter({
      date: { $transform: '$month', $eq: mes },
      'account.offbudget': false,
      amount: { $lt: 0 },
      category: { $ne: null },
      $or: [
        { 'payee.transfer_acct': null },
        { 'payee.transfer_acct.offbudget': true },
      ],
    })
    .select([{ category: { $id: '$category.id' } }, 'amount']);
}

/** Salida más grande de cada categoría (en positivo, céntimos). Pura. */
export function mayorSalidaPorCategoria(
  filas: ReadonlyArray<{ category: string | null; amount: IntegerAmount }>,
): Map<string, IntegerAmount> {
  const mapa = new Map<string, IntegerAmount>();
  for (const { category, amount } of filas) {
    if (category == null || amount >= 0) {
      continue;
    }
    mapa.set(category, Math.max(mapa.get(category) ?? 0, -amount));
  }
  return mapa;
}

/**
 * Salida más grande del mes por categoría, para la regla de «gasto puntual»
 * (`esGastoPuntual`). `undefined` mientras carga.
 */
export function useMayorSalidaDelMes(
  mes: string | undefined,
): ReadonlyMap<string, IntegerAmount> | undefined {
  const { data } = useQuery({
    queryKey: ['informes', 'mayor-salida', mes],
    queryFn: async () => {
      const { data: filas } = await aqlQuery(consultaSalidasDelMes(mes!));
      return mayorSalidaPorCategoria(
        filas as Array<{ category: string | null; amount: IntegerAmount }>,
      );
    },
    enabled: Boolean(mes),
    staleTime: CADUCIDAD_MS,
  });
  return data;
}

function aMovimientos(filas: FilaConsulta[]): Movimiento[] {
  return filas.map<Movimiento>(fila => ({
    mes: fila.date,
    categoria: fila.category,
    payee: fila.payee,
    importe: fila.amount,
  }));
}

/**
 * Movimientos del periodo y, aparte, la parte que no cuenta como ingreso
 * (`excluidos`, ver `noIngreso.ts`). Quien los use debe descontarlos.
 */
export function useMovimientosMensuales(desde: string, hasta: string) {
  return useQuery({
    queryKey: ['informes', 'movimientos', desde, hasta, 'noingreso'],
    queryFn: async () => {
      const [{ data }, { data: excluidos }] = await Promise.all([
        aqlQuery(consultaMovimientos(desde, hasta)),
        aqlQuery(consultaMovimientos(desde, hasta, true)),
      ]);
      return {
        movimientos: aMovimientos(data as FilaConsulta[]),
        excluidos: aMovimientos(excluidos as FilaConsulta[]),
      };
    },
    staleTime: CADUCIDAD_MS,
  });
}

/** Mapa id → info de categoría (nombre, grupo, ingreso, oculta). */
export function useCategoriasInfo(): Categorias {
  const { data: { grouped: grupos } = { grouped: [] } } = useCategories();
  // Las categorías de la historia van ocultas en el Plan, pero cuentan en
  // Informes: son lo que entró y salió antes de Rumbo.
  const [historiaRaw] = useSyncedPref('historia');
  return useMemo(() => {
    const historia = leerHistoria(historiaRaw);
    const deHistoria = new Set(
      [historia?.categoria, historia?.entradas].filter(
        (id): id is string => id != null,
      ),
    );
    const mapa = new Map<string, CategoriaInfo>();
    for (const grupo of grupos) {
      for (const cat of grupo.categories ?? []) {
        mapa.set(cat.id, {
          id: cat.id,
          nombre: cat.name,
          grupo: grupo.id,
          nombreGrupo: grupo.name,
          esIngreso: Boolean(grupo.is_income || cat.is_income),
          oculta:
            !deHistoria.has(cat.id) && Boolean(cat.hidden || grupo.hidden),
        });
      }
    }
    return mapa;
  }, [grupos, historiaRaw]);
}

type Opciones = {
  /** Número de meses, acabando en `hasta` (incluido). */
  meses: number;
  /** Último mes del periodo ('yyyy-MM'); por defecto el actual. */
  hasta?: string;
  incluirOcultas?: boolean;
  /** Categorías que no cuentan. */
  categoriasExcluidas?: ReadonlySet<CategoryEntity['id']>;
};

export type TotalesMensuales = {
  movimientos: Movimiento[];
  categorias: Categorias;
  /** Meses del periodo en orden cronológico. */
  meses: string[];
  isLoading: boolean;
};

/**
 * Movimientos agrupados por mes × categoría × beneficiario para el periodo
 * pedido, sin traspasos ni cuentas fuera de presupuesto, sin lo que no cuenta
 * como ingreso (`noIngreso.ts`), con las categorías
 * ocultas y las excluidas ya filtradas. Con caché compartida entre páginas.
 */
export function useTotalesMensuales({
  meses,
  hasta = monthUtils.currentMonth(),
  incluirOcultas = false,
  categoriasExcluidas,
}: Opciones): TotalesMensuales {
  const desde = monthUtils.subMonths(hasta, Math.max(meses, 1) - 1);
  const { data, isLoading } = useMovimientosMensuales(desde, hasta);
  const categorias = useCategoriasInfo();

  const movimientos = useMemo(() => {
    if (!data) {
      return [];
    }
    const contables = descontarNoIngresos(
      data.movimientos,
      data.excluidos,
      categorias,
    );
    return contables.filter(m => {
      if (m.categoria == null) {
        return true;
      }
      if (categoriasExcluidas?.has(m.categoria)) {
        return false;
      }
      const info = categorias.get(m.categoria);
      return incluirOcultas || !info?.oculta;
    });
  }, [data, categorias, categoriasExcluidas, incluirOcultas]);

  const rango = useMemo(
    () => monthUtils.rangeInclusive(desde, hasta),
    [desde, hasta],
  );

  return { movimientos, categorias, meses: rango, isLoading };
}
